import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MapPin, TrendingUp, ShieldCheck, Zap, Award, Headphones,
  Star, ArrowRight, ChevronLeft, ChevronRight, Search,
  Eye, IndianRupee, Play, CheckCircle, Mail, Send,
  Instagram, Twitter, Linkedin, Facebook, Menu, X, Phone
} from 'lucide-react';
import {
  heroStats, featuredListings, advantages, testimonials,
  trustedBrands, navLinks, footerLinks
} from '../data/LandingPageData';

// ── Styles ─────────────────────────────────────────────────────────────────────
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,400;0,9..144,600;0,9..144,700;0,9..144,800;1,9..144,300;1,9..144,600&family=DM+Sans:wght@300;400;500;600;700&display=swap');

  :root {
    --brand:       #e8621a;
    --brand-dark:  #c2490e;
    --brand-light: #fff4ee;
    --brand-mid:   #fddccc;
    --ink:         #12100e;
    --ink-80:      #2d2926;
    --ink-60:      #6b5e52;
    --ink-40:      #a8998e;
    --ink-10:      #f5f1ee;
    --ink-5:       #faf8f6;
    --white:       #ffffff;
    --shadow-sm:   0 1px 3px rgba(18,16,14,0.08);
    --shadow-md:   0 4px 20px rgba(18,16,14,0.10);
    --shadow-lg:   0 16px 48px rgba(18,16,14,0.14);
    --shadow-xl:   0 32px 80px rgba(18,16,14,0.18);
    --shadow-brand:0 8px 32px rgba(232,98,26,0.30);
  }

  .lp-root * { box-sizing: border-box; margin: 0; padding: 0; }
  .lp-root { font-family: 'DM Sans', sans-serif; color: var(--ink); background: var(--white); overflow-x: hidden; }

  /* Typography */
  .display-xl { font-family: 'Fraunces', serif; font-size: clamp(52px, 8vw, 96px); font-weight: 800; line-height: 0.95; letter-spacing: -0.03em; }
  .display-lg { font-family: 'Fraunces', serif; font-size: clamp(36px, 5vw, 64px); font-weight: 700; line-height: 1.05; letter-spacing: -0.02em; }
  .display-md { font-family: 'Fraunces', serif; font-size: clamp(26px, 3.5vw, 42px); font-weight: 700; line-height: 1.1; letter-spacing: -0.02em; }
  .body-lg { font-size: 17px; line-height: 1.75; color: var(--ink-60); }
  .body-md { font-size: 14px; line-height: 1.65; color: var(--ink-60); }
  .label { font-size: 11px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; }

  /* Animations */
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(28px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50%       { transform: translateY(-10px); }
  }
  @keyframes shimmer {
    0%   { background-position: -200% center; }
    100% { background-position: 200% center; }
  }
  @keyframes pulse-ring {
    0%   { transform: scale(1); opacity: 0.6; }
    100% { transform: scale(1.8); opacity: 0; }
  }
  @keyframes scroll-indicator {
    0%, 100% { transform: translateY(0); opacity: 1; }
    50%       { transform: translateY(6px); opacity: 0.4; }
  }
  @keyframes marquee {
    from { transform: translateX(0); }
    to   { transform: translateX(-50%); }
  }

  .anim-fade-up { animation: fadeUp 0.7s ease both; }
  .anim-fade-up-1 { animation: fadeUp 0.7s ease 0.1s both; }
  .anim-fade-up-2 { animation: fadeUp 0.7s ease 0.2s both; }
  .anim-fade-up-3 { animation: fadeUp 0.7s ease 0.3s both; }
  .anim-fade-up-4 { animation: fadeUp 0.7s ease 0.4s both; }
  .anim-fade-in  { animation: fadeIn 1s ease both; }

  /* Nav */
  .lp-nav { position: fixed; top: 0; left: 0; right: 0; z-index: 1000; transition: all 0.3s ease; }
  .lp-nav.scrolled { background: rgba(255,255,255,0.96); backdrop-filter: blur(16px); border-bottom: 1px solid rgba(18,16,14,0.08); box-shadow: var(--shadow-sm); }
  .lp-nav.top { background: transparent; }
  .nav-link { color: rgba(255,255,255,0.8); font-size: 14px; font-weight: 500; text-decoration: none; transition: color 0.2s; }
  .nav-link:hover { color: #fff; }
  .lp-nav.scrolled .nav-link { color: var(--ink-60); }
  .lp-nav.scrolled .nav-link:hover { color: var(--brand); }

  /* Buttons */
  .btn-primary {
    display: inline-flex; align-items: center; gap: 8px;
    background: linear-gradient(135deg, var(--brand), var(--brand-dark));
    color: #fff; border: none; border-radius: 14px;
    padding: 14px 28px; font-family: 'DM Sans', sans-serif;
    font-weight: 600; font-size: 15px; cursor: pointer;
    box-shadow: var(--shadow-brand); transition: all 0.22s ease;
    text-decoration: none; white-space: nowrap;
  }
  .btn-primary:hover { transform: translateY(-2px); box-shadow: 0 12px 40px rgba(232,98,26,0.4); }
  .btn-primary:active { transform: translateY(0); }

  .btn-outline {
    display: inline-flex; align-items: center; gap: 8px;
    background: transparent; color: var(--ink-80);
    border: 1.5px solid rgba(18,16,14,0.2); border-radius: 14px;
    padding: 13px 26px; font-family: 'DM Sans', sans-serif;
    font-weight: 500; font-size: 15px; cursor: pointer;
    transition: all 0.22s ease; text-decoration: none; white-space: nowrap;
  }
  .btn-outline:hover { border-color: var(--brand); color: var(--brand); background: var(--brand-light); }
  .btn-outline-white {
    display: inline-flex; align-items: center; gap: 8px;
    background: transparent; color: rgba(255,255,255,0.85);
    border: 1.5px solid rgba(255,255,255,0.3); border-radius: 14px;
    padding: 13px 26px; font-family: 'DM Sans', sans-serif;
    font-weight: 500; font-size: 15px; cursor: pointer;
    transition: all 0.22s ease; text-decoration: none;
  }
  .btn-outline-white:hover { border-color: rgba(255,255,255,0.7); color: #fff; background: rgba(255,255,255,0.1); }

  /* Cards */
  .listing-card {
    background: var(--white); border-radius: 20px; overflow: hidden;
    border: 1px solid rgba(18,16,14,0.08); box-shadow: var(--shadow-md);
    transition: all 0.28s ease; cursor: pointer;
  }
  .listing-card:hover { transform: translateY(-6px); box-shadow: var(--shadow-xl); border-color: var(--brand-mid); }

  .advantage-card {
    background: var(--white); border-radius: 18px; padding: 28px;
    border: 1px solid rgba(18,16,14,0.07); box-shadow: var(--shadow-sm);
    transition: all 0.25s ease;
  }
  .advantage-card:hover { transform: translateY(-4px); box-shadow: var(--shadow-lg); border-color: var(--brand-mid); }

  /* Section */
  .section { padding: clamp(60px, 8vw, 120px) clamp(20px, 5vw, 80px); }
  .section-inner { max-width: 1240px; margin: 0 auto; }
  .section-tag { color: var(--brand); display: inline-block; margin-bottom: 14px; }

  /* Hero specific */
  .hero-grain::after {
    content: ''; position: absolute; inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.03'/%3E%3C/svg%3E");
    pointer-events: none; opacity: 0.4;
  }

  /* Testimonial */
  .testimonial-dot { width: 8px; height: 8px; border-radius: 50%; cursor: pointer; transition: all 0.2s; }

  /* Marquee */
  .marquee-track { display: flex; width: max-content; animation: marquee 22s linear infinite; }
  .marquee-track:hover { animation-play-state: paused; }

  /* Stats counter */
  .stat-num { font-family: 'Fraunces', serif; font-size: clamp(36px, 5vw, 56px); font-weight: 800; color: var(--ink); letter-spacing: -0.03em; line-height: 1; }

  /* Input */
  .lp-input {
    background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.15);
    border-radius: 12px; padding: 12px 16px; color: #fff;
    font-family: 'DM Sans', sans-serif; font-size: 14px;
    outline: none; transition: all 0.2s; width: 100%;
  }
  .lp-input::placeholder { color: rgba(255,255,255,0.35); }
  .lp-input:focus { border-color: var(--brand); background: rgba(255,255,255,0.12); }

  .lp-input-light {
    background: var(--ink-5); border: 1.5px solid rgba(18,16,14,0.1);
    border-radius: 12px; padding: 13px 16px; color: var(--ink);
    font-family: 'DM Sans', sans-serif; font-size: 14px;
    outline: none; transition: all 0.2s; width: 100%;
  }
  .lp-input-light::placeholder { color: var(--ink-40); }
  .lp-input-light:focus { border-color: var(--brand); background: #fff; box-shadow: 0 0 0 3px rgba(232,98,26,0.1); }

  /* Scroll indicator */
  .scroll-dot { width: 5px; height: 5px; border-radius: 50%; background: rgba(255,255,255,0.6); animation: scroll-indicator 1.4s ease infinite; }

  @media (max-width: 768px) {
    .hide-mobile { display: none !important; }
    .section { padding: 60px 20px; }
  }
  @media (min-width: 769px) {
    .show-mobile { display: none !important; }
  }
`;

// ── Icon helper ────────────────────────────────────────────────────────────────
const IconMap: Record<string, React.ReactNode> = {
  MapPin: <MapPin size={22}/>, TrendingUp: <TrendingUp size={22}/>,
  ShieldCheck: <ShieldCheck size={22}/>, Zap: <Zap size={22}/>,
  Award: <Award size={22}/>, Headphones: <Headphones size={22}/>,
};

// ── Navbar ─────────────────────────────────────────────────────────────────────
const LandingNav: React.FC<{ onLogin: ()=>void; onRegister: ()=>void }> = ({ onLogin, onRegister }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const logoPath = "/adrojlogo.png";

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  return (
    <nav className={`lp-nav ${scrolled ? 'scrolled' : 'top'}`}>
      <div style={{ maxWidth: 1240, margin: '0 auto', padding: '0 clamp(20px,4vw,48px)', height: 68, display: 'flex', alignItems: 'center', gap: 0 }}>
        {/* Logo */}
        <a href="#home" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 10, marginRight: 48 }}>
          {/* <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg,#e8621a,#c2490e)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(232,98,26,0.35)' }}>
            <span style={{ color: '#fff', fontFamily: 'Fraunces, serif', fontWeight: 800, fontSize: 20 }}>A</span>
          </div> */}
          <img
            src={logoPath}
            alt="Adroj Logo"
            style={{
              width: 48,
              height: 48,
              objectFit: 'contain'
            }}
          />
         <span
          style={{
            fontFamily: 'Fraunces, serif',
            fontWeight: 700,
            fontSize: 20,
            color: scrolled ? 'var(--ink)' : '#fff',
            letterSpacing: '-0.02em'
          }}
        >
          Adroj
        </span>
        </a>

        {/* Desktop Nav */}
        <div className="hide-mobile" style={{ display: 'flex', gap: 4, flex: 1 }}>
          {navLinks.map(l => (
            <a key={l.label} href={l.href} className="nav-link" style={{ padding: '7px 13px', borderRadius: 9 }}>{l.label}</a>
          ))}
        </div>

        <div className="hide-mobile" style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 'auto' }}>
          <button onClick={onLogin} className="btn-outline-white" style={scrolled ? { color: 'var(--ink-60)', borderColor: 'rgba(18,16,14,0.15)', background: 'transparent', padding: '9px 20px', fontSize: 14 } : { padding: '9px 20px', fontSize: 14 }}>
            Sign In
          </button>
          <button onClick={onRegister} className="btn-primary" style={{ padding: '10px 22px', fontSize: 14 }}>
            List Hoarding
          </button>
        </div>

        {/* Mobile hamburger */}
        <button className="show-mobile" onClick={() => setMobileOpen(!mobileOpen)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: scrolled ? 'var(--ink)' : '#fff', marginLeft: 'auto', padding: 4 }}>
          {mobileOpen ? <X size={22}/> : <Menu size={22}/>}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div style={{ background: '#fff', borderTop: '1px solid rgba(18,16,14,0.08)', padding: '16px 20px 24px' }}>
          {navLinks.map(l => (
            <a key={l.label} href={l.href} onClick={() => setMobileOpen(false)} style={{ display: 'block', padding: '12px 0', color: 'var(--ink-60)', fontSize: 15, fontWeight: 500, textDecoration: 'none', borderBottom: '1px solid rgba(18,16,14,0.06)' }}>{l.label}</a>
          ))}
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button onClick={onLogin} className="btn-outline" style={{ flex: 1, justifyContent: 'center', padding: '11px' }}>Sign In</button>
            <button onClick={onRegister} className="btn-primary" style={{ flex: 1, justifyContent: 'center', padding: '11px' }}>List Hoarding</button>
          </div>
        </div>
      )}
    </nav>
  );
};

// ── Hero ───────────────────────────────────────────────────────────────────────
const HeroSection: React.FC<{ onExplore: ()=>void }> = ({ onExplore }) => {
  const [searchQuery, setSearchQuery] = useState('');

  return (
    <section id="home" className="hero-grain" style={{ position: 'relative', minHeight: '100vh', display: 'flex', alignItems: 'center', background: 'linear-gradient(160deg, #0e0b09 0%, #1f1208 40%, #0e0b09 100%)', overflow: 'hidden' }}>
      {/* Background image overlay */}
      <div style={{ position: 'absolute', inset: 0, backgroundImage: 'url(https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1600&q=60)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.08 }}/>

      {/* Radial glow */}
      <div style={{ position: 'absolute', top: '20%', left: '50%', transform: 'translateX(-50%)', width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(232,98,26,0.18) 0%, transparent 70%)', pointerEvents: 'none' }}/>

      {/* Decorative circles */}
      <div style={{ position: 'absolute', top: 120, right: '8%', width: 280, height: 280, borderRadius: '50%', border: '1px solid rgba(232,98,26,0.12)', animation: 'float 6s ease infinite' }}/>
      <div style={{ position: 'absolute', top: 180, right: '11%', width: 160, height: 160, borderRadius: '50%', border: '1px solid rgba(232,98,26,0.08)' }}/>
      <div style={{ position: 'absolute', bottom: '15%', left: '5%', width: 200, height: 200, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.05)' }}/>

      {/* Floating stat cards */}
      <div className="hide-mobile" style={{ position: 'absolute', top: '28%', right: '6%', background: 'rgba(255,255,255,0.06)', backdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 16, padding: '14px 18px', animation: 'float 5s ease infinite', animationDelay: '1s' }}>
        <div style={{ color: '#e8621a', fontFamily: 'Fraunces, serif', fontWeight: 700, fontSize: 22 }}>12K+</div>
        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: 500, marginTop: 2 }}>Active Advertisers</div>
      </div>
      <div className="hide-mobile" style={{ position: 'absolute', bottom: '30%', right: '10%', background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, padding: '12px 16px', animation: 'float 7s ease infinite', animationDelay: '2s' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#10b981', boxShadow: '0 0 8px #10b981' }}/>
          <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>2,400+ Spaces Live</span>
        </div>
      </div>

      <div style={{ maxWidth: 1240, margin: '0 auto', padding: '0 clamp(20px,5vw,80px)', width: '100%', paddingTop: 100, paddingBottom: 80 }}>
        <div style={{ maxWidth: 780 }}>
          {/* Tag */}
          <div className="anim-fade-up" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(232,98,26,0.15)', border: '1px solid rgba(232,98,26,0.3)', borderRadius: 30, padding: '6px 14px', marginBottom: 28 }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#e8621a', boxShadow: '0 0 8px #e8621a' }}/>
            <span style={{ color: '#fba071', fontSize: 12, fontWeight: 600, letterSpacing: '0.06em' }}>INDIA'S #1 OUTDOOR AD MARKETPLACE</span>
          </div>

          {/* Headline */}
          <h1 className="display-xl anim-fade-up-1" style={{ color: '#fff', marginBottom: 24 }}>
            Advertise<br/>
            <span style={{ color: '#e8621a', fontStyle: 'italic' }}>Outdoors.</span><br/>
            <span style={{ color: 'rgba(255,255,255,0.85)' }}>Smarter.</span>
          </h1>

          <p className="body-lg anim-fade-up-2" style={{ color: 'rgba(255,255,255,0.55)', maxWidth: 520, marginBottom: 36, fontSize: 16 }}>
            Find the perfect hoarding, billboard, or outdoor space for your brand. Connect directly with owners. Book in minutes.
          </p>

          {/* Search bar */}
          <div className="anim-fade-up-3" style={{ display: 'flex', gap: 10, marginBottom: 32, maxWidth: 540, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200, position: 'relative' }}>
              <Search size={15} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.35)' }}/>
              <input className="lp-input" placeholder="Search city, area or landmark..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} style={{ paddingLeft: 40, borderRadius: 14 }}/>
            </div>
            <button onClick={onExplore} className="btn-primary" style={{ padding: '12px 24px' }}>
              Explore <ArrowRight size={15}/>
            </button>
          </div>

          {/* CTA buttons */}
          <div className="anim-fade-up-3" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 48 }}>
            <button onClick={onExplore} className="btn-primary" style={{ fontSize: 15 }}>
              Explore Hoardings <ArrowRight size={16}/>
            </button>
            <button className="btn-outline-white" style={{ fontSize: 15 }}>
              <Play size={15} style={{ fill: 'currentColor' }}/> Contact Sales
            </button>
          </div>

          {/* Trust badges */}
          <div className="anim-fade-up-4" style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            {[
              { icon: '✓', text: '10,000+ Happy Clients' },
              { icon: '★', text: '4.8/5 Rating' },
              { icon: '✓', text: '100% Verified Listings' },
            ].map(b => (
              <div key={b.text} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ color: '#e8621a', fontWeight: 700, fontSize: 13 }}>{b.icon}</span>
                <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12, fontWeight: 500 }}>{b.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div style={{ position: 'absolute', bottom: 32, left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
        <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Scroll</span>
        <div style={{ width: 1, height: 40, background: 'linear-gradient(to bottom, rgba(232,98,26,0.6), transparent)' }}/>
      </div>
    </section>
  );
};

// ── Stats ──────────────────────────────────────────────────────────────────────
const StatsSection: React.FC = () => (
  <section style={{ background: 'var(--ink)', padding: '60px clamp(20px,5vw,80px)' }}>
    <div style={{ maxWidth: 1240, margin: '0 auto' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 0 }}>
        {heroStats.map((s, i) => (
          <div key={s.label} style={{ textAlign: 'center', padding: '20px 16px', borderRight: i < heroStats.length - 1 ? '1px solid rgba(255,255,255,0.07)' : 'none' }}>
            <div className="stat-num" style={{ color: i === 0 ? '#e8621a' : i === 1 ? '#fff' : i === 2 ? '#fff' : '#e8621a' }}>{s.value}</div>
            <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 12, fontWeight: 500, marginTop: 8, letterSpacing: '0.04em' }}>{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ── Trusted Brands Marquee ─────────────────────────────────────────────────────
const BrandsMarquee: React.FC = () => {
  const repeated = [...trustedBrands, ...trustedBrands, ...trustedBrands, ...trustedBrands];
  return (
    <section style={{ padding: '36px 0', background: 'var(--ink-5)', borderTop: '1px solid rgba(18,16,14,0.06)', borderBottom: '1px solid rgba(18,16,14,0.06)', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 12 }}>
        <div style={{ height: 1, flex: 1, background: 'var(--ink-10)' }}/>
        <span className="label" style={{ color: 'var(--ink-40)', whiteSpace: 'nowrap' }}>Trusted by India's leading brands</span>
        <div style={{ height: 1, flex: 1, background: 'var(--ink-10)' }}/>
      </div>
      <div style={{ overflow: 'hidden' }}>
        <div className="marquee-track">
          {repeated.map((brand, i) => (
            <div key={i} style={{ flexShrink: 0, marginRight: 48, display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#e8621a', opacity: 0.4 }}/>
              <span style={{ fontFamily: 'Fraunces, serif', fontWeight: 600, fontSize: 18, color: 'var(--ink-40)', whiteSpace: 'nowrap' }}>{brand}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ── Featured Listings ──────────────────────────────────────────────────────────
const FeaturedListings: React.FC<{ onExplore: ()=>void }> = ({ onExplore }) => (
  <section id="listings" className="section" style={{ background: '#fff' }}>
    <div className="section-inner">
      <div style={{ textAlign: 'center', marginBottom: 56 }}>
        <span className="label section-tag">Featured Listings</span>
        <h2 className="display-lg" style={{ marginBottom: 16 }}>Premium Advertising Spaces</h2>
        <p className="body-lg" style={{ maxWidth: 520, margin: '0 auto' }}>Hand-picked high-visibility locations across India's prime locations</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 24 }}>
        {featuredListings.map((listing, i) => (
          <div key={listing.id} className="listing-card" style={{ animationDelay: `${i * 0.1}s` }}>
            {/* Image */}
            <div style={{ position: 'relative', height: 220, overflow: 'hidden' }}>
              <img src={listing.image} alt={listing.title} style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.4s ease' }}
                onMouseEnter={e => (e.currentTarget as HTMLImageElement).style.transform = 'scale(1.06)'}
                onMouseLeave={e => (e.currentTarget as HTMLImageElement).style.transform = 'scale(1)'}/>
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(18,16,14,0.6) 0%, transparent 55%)' }}/>
              <div style={{ position: 'absolute', top: 14, left: 14, background: listing.badgeColor, color: '#fff', padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700, letterSpacing: '0.04em' }}>{listing.badge}</div>
              <div style={{ position: 'absolute', top: 14, right: 14, background: 'rgba(18,16,14,0.6)', backdropFilter: 'blur(8px)', color: '#fff', padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 500 }}>{listing.mediaType}</div>
              {/* Eye count */}
              <div style={{ position: 'absolute', bottom: 14, left: 14, display: 'flex', alignItems: 'center', gap: 5, color: 'rgba(255,255,255,0.8)', fontSize: 12 }}>
                <Eye size={13}/> {listing.dailyViews} views/day
              </div>
              {/* Price */}
              <div style={{ position: 'absolute', bottom: 14, right: 14, background: 'var(--brand)', color: '#fff', padding: '4px 12px', borderRadius: 20, fontFamily: 'Fraunces, serif', fontWeight: 700, fontSize: 15 }}>
                ₹{listing.price.toLocaleString('en-IN')}
              </div>
            </div>

            {/* Body */}
            <div style={{ padding: '18px 20px' }}>
              <h3 style={{ fontWeight: 700, fontSize: 15, color: 'var(--ink)', marginBottom: 5, lineHeight: 1.3 }}>{listing.title}</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--ink-60)', fontSize: 12, marginBottom: 14 }}>
                <MapPin size={12} color="#e8621a"/> {listing.location}
              </div>

              {/* Metrics */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
                {[
                  { label: 'Size', val: listing.size },
                  { label: 'Daily Traffic', val: listing.dailyTraffic },
                  { label: 'Reviews', val: listing.reviews },
                ].map(m => (
                  <div key={m.label} style={{ background: 'var(--ink-5)', borderRadius: 9, padding: '8px 10px', textAlign: 'center' }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--ink)' }}>{m.val}</div>
                    <div style={{ fontSize: 10, color: 'var(--ink-40)', marginTop: 2, fontWeight: 500 }}>{m.label}</div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} size={13} style={{ fill: j < Math.floor(listing.rating) ? '#f59e0b' : 'none', color: '#f59e0b' }}/>
                  ))}
                  <span style={{ fontSize: 12, color: 'var(--ink-60)', marginLeft: 3 }}>{listing.rating}</span>
                </div>
                <button className="btn-primary" style={{ padding: '8px 16px', fontSize: 13, borderRadius: 10 }}>
                  View Details <ArrowRight size={13}/>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ textAlign: 'center', marginTop: 48 }}>
        <button onClick={onExplore} className="btn-outline" style={{ fontSize: 15, padding: '13px 32px' }}>
          View All Listings <ArrowRight size={16}/>
        </button>
      </div>
    </div>
  </section>
);

// ── Advantages ─────────────────────────────────────────────────────────────────
const AdvantagesSection: React.FC = () => (
  <section className="section" style={{ background: 'var(--ink-5)' }}>
    <div className="section-inner">
      <div style={{ textAlign: 'center', marginBottom: 56 }}>
        <span className="label section-tag">Why Choose Us</span>
        <h2 className="display-lg" style={{ marginBottom: 16 }}>The Adroj Advantage</h2>
        <p className="body-lg" style={{ maxWidth: 480, margin: '0 auto' }}>Experience the future of outdoor advertising with our innovative platform</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
        {advantages.map((adv, i) => (
          <div key={adv.title} className="advantage-card">
            <div style={{ width: 52, height: 52, borderRadius: 14, background: `${adv.color}12`, border: `1px solid ${adv.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: adv.color, marginBottom: 18 }}>
              {IconMap[adv.icon]}
            </div>
            <h3 style={{ fontWeight: 700, fontSize: 16, color: 'var(--ink)', marginBottom: 8 }}>{adv.title}</h3>
            <p className="body-md">{adv.description}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ── Testimonials ───────────────────────────────────────────────────────────────
const TestimonialsSection: React.FC = () => {
  const [active, setActive] = useState(0);
  const t = testimonials[active];

  return (
    <section className="section" style={{ background: '#fff', overflow: 'hidden' }}>
      <div className="section-inner">
        <div style={{ textAlign: 'center', marginBottom: 56 }}>
          <span className="label section-tag">Testimonials</span>
          <h2 className="display-lg" style={{ marginBottom: 12 }}>What Our Clients Say</h2>
          <p className="body-lg" style={{ maxWidth: 400, margin: '0 auto' }}>Trusted by leading brands across India</p>
        </div>

        <div style={{ maxWidth: 760, margin: '0 auto', position: 'relative' }}>
          {/* Quote card */}
          <div style={{ background: 'linear-gradient(135deg, var(--ink) 0%, #2d1a0a 100%)', borderRadius: 28, padding: 'clamp(32px,5vw,56px)', position: 'relative', overflow: 'hidden', minHeight: 280 }}>
            {/* Decorative */}
            <div style={{ position: 'absolute', top: -40, right: -40, width: 200, height: 200, borderRadius: '50%', border: '1px solid rgba(232,98,26,0.15)' }}/>
            <div style={{ position: 'absolute', bottom: -60, left: -30, width: 180, height: 180, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.04)' }}/>

            {/* Quote mark */}
            <div style={{ fontFamily: 'Fraunces, serif', fontSize: 80, color: 'rgba(232,98,26,0.2)', lineHeight: 0.7, marginBottom: 24, position: 'relative', zIndex: 1 }}>"</div>

            {/* Stars */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 20, position: 'relative', zIndex: 1 }}>
              {[...Array(t.rating)].map((_, j) => (
                <Star key={j} size={18} style={{ fill: '#f59e0b', color: '#f59e0b' }}/>
              ))}
            </div>

            <p style={{ fontFamily: 'Fraunces, serif', fontStyle: 'italic', fontSize: 'clamp(17px,2.2vw,22px)', color: '#fff', lineHeight: 1.55, marginBottom: 32, position: 'relative', zIndex: 1, fontWeight: 300 }}>
              "{t.quote}"
            </p>

            <div style={{ display: 'flex', alignItems: 'center', gap: 14, position: 'relative', zIndex: 1 }}>
              <div style={{ width: 46, height: 46, borderRadius: '50%', background: `${t.color}30`, border: `2px solid ${t.color}60`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: t.color, fontFamily: 'Fraunces, serif', fontWeight: 700, fontSize: 16 }}>{t.avatar}</div>
              <div>
                <div style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>{t.author}</div>
                <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, marginTop: 2 }}>{t.role}</div>
              </div>
            </div>
          </div>

          {/* Nav */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 28, padding: '0 4px' }}>
            <button onClick={() => setActive(a => (a - 1 + testimonials.length) % testimonials.length)} style={{ width: 42, height: 42, borderRadius: '50%', background: '#fff', border: '1.5px solid rgba(18,16,14,0.12)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-60)', transition: 'all 0.2s', boxShadow: 'var(--shadow-sm)' }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--brand)'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--brand)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(18,16,14,0.12)'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--ink-60)'; }}>
              <ChevronLeft size={18}/>
            </button>
            <div style={{ display: 'flex', gap: 8 }}>
              {testimonials.map((_, i) => (
                <button key={i} onClick={() => setActive(i)} className="testimonial-dot" style={{ background: i === active ? 'var(--brand)' : 'var(--ink-10)', transform: i === active ? 'scale(1.4)' : 'scale(1)', border: 'none', cursor: 'pointer' }}/>
              ))}
            </div>
            <button onClick={() => setActive(a => (a + 1) % testimonials.length)} style={{ width: 42, height: 42, borderRadius: '50%', background: '#fff', border: '1.5px solid rgba(18,16,14,0.12)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-60)', transition: 'all 0.2s', boxShadow: 'var(--shadow-sm)' }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--brand)'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--brand)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(18,16,14,0.12)'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--ink-60)'; }}>
              <ChevronRight size={18}/>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

// ── Contact ────────────────────────────────────────────────────────────────────
const ContactSection: React.FC = () => {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await new Promise(r => setTimeout(r, 800));
    setSent(true);
    setTimeout(() => setSent(false), 3000);
  };

  return (
    <section id="contact" className="section" style={{ background: 'var(--ink)', position: 'relative', overflow: 'hidden' }}>
      {/* Glow */}
      <div style={{ position: 'absolute', top: 0, left: '30%', width: 500, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(232,98,26,0.1) 0%, transparent 70%)', pointerEvents: 'none' }}/>

      <div className="section-inner" style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 64, alignItems: 'start' }}>
          {/* Left */}
          <div>
            <span className="label" style={{ color: 'rgba(232,98,26,0.8)', marginBottom: 16, display: 'block' }}>Get in Touch</span>
            <h2 className="display-md" style={{ color: '#fff', marginBottom: 16, lineHeight: 1.15 }}>Let's Discuss Your Campaign</h2>
            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 15, lineHeight: 1.7, marginBottom: 36 }}>Have questions about our platform? Our team is here to help you succeed.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {[
                { icon: <Mail size={16}/>, text: 'support@adroj.com' },
                { icon: <Phone size={16}/>, text: '+91 98765 43210' },
                { icon: <MapPin size={16}/>, text: 'Mumbai, Maharashtra, India' },
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(232,98,26,0.12)', border: '1px solid rgba(232,98,26,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#e8621a' }}>{item.icon}</div>
                  <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14 }}>{item.text}</span>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 36 }}>
              <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 14 }}>Follow Us</div>
              <div style={{ display: 'flex', gap: 10 }}>
                {[Instagram, Twitter, Linkedin, Facebook].map((Icon, i) => (
                  <button key={i} style={{ width: 38, height: 38, borderRadius: 10, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', transition: 'all 0.2s' }}
                    onMouseEnter={e => { (e.currentTarget).style.background = 'rgba(232,98,26,0.15)'; (e.currentTarget).style.color = '#e8621a'; (e.currentTarget).style.borderColor = 'rgba(232,98,26,0.3)'; }}
                    onMouseLeave={e => { (e.currentTarget).style.background = 'rgba(255,255,255,0.06)'; (e.currentTarget).style.color = 'rgba(255,255,255,0.4)'; (e.currentTarget).style.borderColor = 'rgba(255,255,255,0.08)'; }}>
                    <Icon size={16}/>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 24, padding: 'clamp(24px,4vw,40px)' }}>
            <h3 style={{ color: '#fff', fontWeight: 700, fontSize: 20, marginBottom: 24 }}>Send a Message</h3>
            {sent ? (
              <div style={{ textAlign: 'center', padding: '32px 0' }}>
                <CheckCircle size={48} style={{ color: '#10b981', marginBottom: 14 }}/>
                <p style={{ color: '#fff', fontWeight: 600, fontSize: 16 }}>Message sent successfully!</p>
                <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13, marginTop: 6 }}>We'll get back to you within 24 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', display: 'block', marginBottom: 7 }}>Full Name *</label>
                  <input className="lp-input" placeholder="Enter your full name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required/>
                </div>
                <div>
                  <label style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', display: 'block', marginBottom: 7 }}>Email Address *</label>
                  <input className="lp-input" type="email" placeholder="Enter your email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required/>
                </div>
                <div>
                  <label style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', display: 'block', marginBottom: 7 }}>Message *</label>
                  <textarea className="lp-input" placeholder="Tell us about your advertising needs..." rows={4} value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} required style={{ resize: 'vertical', minHeight: 100 }}/>
                </div>
                <button type="submit" className="btn-primary" style={{ marginTop: 6, justifyContent: 'center' }}>
                  Send Message <Send size={15}/>
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// ── Newsletter ─────────────────────────────────────────────────────────────────
const NewsletterSection: React.FC = () => {
  const [email, setEmail] = useState('');
  const [subbed, setSubbed] = useState(false);
  return (
    <section style={{ background: 'linear-gradient(135deg, #1a0a03 0%, var(--ink) 100%)', padding: '72px clamp(20px,5vw,80px)', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
      <div style={{ maxWidth: 600, margin: '0 auto', textAlign: 'center' }}>
        <div style={{ width: 56, height: 56, borderRadius: 16, background: 'rgba(232,98,26,0.15)', border: '1px solid rgba(232,98,26,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', color: '#e8621a' }}>
          <Mail size={24}/>
        </div>
        <h2 className="display-md" style={{ color: '#fff', marginBottom: 12 }}>Stay Updated</h2>
        <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 15, lineHeight: 1.65, marginBottom: 32 }}>Get the latest updates on new hoardings, special offers, and industry insights.</p>
        {subbed ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, color: '#10b981' }}>
            <CheckCircle size={20}/> <span style={{ fontWeight: 600 }}>Subscribed successfully!</span>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 10, maxWidth: 440, margin: '0 auto', flexWrap: 'wrap' }}>
            <input className="lp-input" type="email" placeholder="Enter your email address" value={email} onChange={e => setEmail(e.target.value)} style={{ flex: 1, minWidth: 200 }}/>
            <button onClick={() => { if (email) { setSubbed(true); }}} className="btn-primary" style={{ padding: '12px 22px', whiteSpace: 'nowrap' }}>
              Subscribe
            </button>
          </div>
        )}
        <p style={{ color: 'rgba(255,255,255,0.2)', fontSize: 12, marginTop: 14 }}>No spam, unsubscribe at any time.</p>
      </div>
    </section>
  );
};

// ── Footer ─────────────────────────────────────────────────────────────────────
const Footer: React.FC<{ onLogin: ()=>void }> = ({ onLogin }) => { 
const logoPath = "/adrojlogo.png";
  return(
  <footer style={{ background: '#080604', padding: 'clamp(48px,6vw,80px) clamp(20px,5vw,80px) 32px', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
    <div style={{ maxWidth: 1240, margin: '0 auto' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 48, marginBottom: 56 }}>
        {/* Brand */}
        <div style={{ gridColumn: 'span 1' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            {/* <div style={{ width: 34, height: 34, borderRadius: 10, background: 'linear-gradient(135deg,#e8621a,#c2490e)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ color: '#fff', fontFamily: 'Fraunces, serif', fontWeight: 800, fontSize: 18 }}>A</span>
            </div> */}
            <img
            src={logoPath}
            alt="Adroj Logo"
            style={{
              width: 55,
              height: 55,
              objectFit: 'contain'
            }}
          />
            <span
            style={{
              fontFamily: 'Fraunces, serif',
              fontWeight: 700,
              fontSize: 18,
              color: '#fff'
            }}
          >
            Adroj
          </span>
            {/* <span style={{ fontFamily: 'Fraunces, serif', fontWeight: 700, fontSize: 18, color: '#fff' }}>adroj<span style={{ color: '#e8621a' }}>.</span></span> */}
          </div>
          <p style={{ color: 'rgba(255,255,255,0.25)', fontSize: 13, lineHeight: 1.7, maxWidth: 220, marginBottom: 20 }}>India's #1 outdoor advertising marketplace. Connecting brands with premium spaces.</p>
          <div style={{ display: 'flex', gap: 8 }}>
            {[Instagram, Twitter, Linkedin, Facebook].map((Icon, i) => (
              <button key={i} style={{ width: 34, height: 34, borderRadius: 9, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.3)', cursor: 'pointer', transition: 'all 0.2s' }}
                onMouseEnter={e => { (e.currentTarget).style.color = '#e8621a'; (e.currentTarget).style.borderColor = 'rgba(232,98,26,0.3)'; }}
                onMouseLeave={e => { (e.currentTarget).style.color = 'rgba(255,255,255,0.3)'; (e.currentTarget).style.borderColor = 'rgba(255,255,255,0.06)'; }}>
                <Icon size={14}/>
              </button>
            ))}
          </div>
        </div>

        {/* Links */}
        {Object.entries(footerLinks).map(([title, links]) => (
          <div key={title}>
            <div style={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600, fontSize: 13, marginBottom: 18, letterSpacing: '0.04em' }}>{title}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {links.map(link => (
                <a key={link} href="#" style={{ color: 'rgba(255,255,255,0.28)', fontSize: 13, textDecoration: 'none', transition: 'color 0.2s' }}
                  onMouseEnter={e => (e.currentTarget as HTMLAnchorElement).style.color = '#e8621a'}
                  onMouseLeave={e => (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(255,255,255,0.28)'}>{link}</a>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div style={{ borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <p style={{ color: 'rgba(255,255,255,0.18)', fontSize: 12 }}>© 2026 Adroj. All rights reserved.(V-1.0.0)</p>
        <button onClick={onLogin} className="btn-primary" style={{ padding: '9px 20px', fontSize: 13 }}>
          Sign In to Portal
        </button>
      </div>
    </div>
  </footer>  );
};

// ── Main Landing Page ──────────────────────────────────────────────────────────
const LandingPage: React.FC = () => {
  const navigate = useNavigate();

  const goToLogin = () => navigate('/login');
  const goToRegister = () => navigate('/register');
  const goToSearch = () => navigate('/login');

  return (
    <>
      <style>{styles}</style>
      <div className="lp-root">
        <LandingNav onLogin={goToLogin} onRegister={goToRegister}/>
        <HeroSection onExplore={goToSearch}/>
        <StatsSection/>
        <BrandsMarquee/>
        <FeaturedListings onExplore={goToSearch}/>
        <AdvantagesSection/>
        <TestimonialsSection/>
        <ContactSection/>
        <NewsletterSection/>
        <Footer onLogin={goToLogin}/>
      </div>
    </>
  );
};

export default LandingPage;
